import {
  type QuotePlan,
  type QuotePlanType,
} from '../constants/quizQuote.const';

import { type FirstPurchasePromotion } from './quoteCalculator';
import { formatDiscountRate } from './quoteFormat';

/** 그룹 요금제 조건에 1스텝 근접했을 때 노출하는 유도 문구. */
export const UPSELL_ADD_USER = '1명만 추가하면 더 저렴하게 이용할 수 있어요.';
export const UPSELL_EXTEND_TERM =
  '1개월만 늘리면 더 저렴하게 이용할 수 있어요.';

export interface QuoteUpsellInput {
  plan: QuotePlan;
  planType: QuotePlanType;
  term: number;
  userCount: number;
  minUserCount: number;
  minMonthCount: number;
}

/**
 * Basic 첫 구매 할인의 할인 상한을 알리는 문구. 초과분은 정가로 계산된다.
 * 문구의 개월수·할인율은 서버 정책(`activePromotions`)에서 온다 — 하드코딩하지 않는다.
 */
export function getFirstPurchaseUpsellMessage({
  plan,
  term,
  promotion,
}: {
  plan: QuotePlan;
  term: number;
  promotion: FirstPurchasePromotion | null;
}): string | null {
  if (plan !== 'basic' || promotion === null) return null;

  const { maxDiscountMonths, discountRate } = promotion;
  if (maxDiscountMonths === null || term <= maxDiscountMonths) return null;

  return `${formatDiscountRate(
    discountRate,
  )} 할인은 첫 ${maxDiscountMonths}개월까지 적용되고, 이후 기간은 정상 가격이에요.`;
}

/**
 * 개인 요금제로 선택했지만 전달받은 학교·단체(그룹) 할인 조건에
 * "딱 1스텝" 모자란 경우 유도 배너 문구를 반환한다. 해당 없으면 null.
 * - 기간은 minMonthCount 이상·인원만 minUserCount보다 1명 부족 → 인원 추가 유도
 * - 인원은 minUserCount 이상·기간만 minMonthCount보다 1개월 부족 → 기간 연장 유도
 */
export function getUpsellMessage({
  plan,
  planType,
  term,
  userCount,
  minUserCount,
  minMonthCount,
}: QuoteUpsellInput): string | null {
  // Basic 은 학교·단체 할인 대상이 아니다 — 조건을 채워도 더 싸지지 않는다.
  if (plan === 'basic') return null;
  if (planType !== 'individual') return null;

  if (term >= minMonthCount && userCount === minUserCount - 1) {
    return UPSELL_ADD_USER;
  }
  if (term === minMonthCount - 1 && userCount >= minUserCount) {
    return UPSELL_EXTEND_TERM;
  }
  return null;
}
